/* eslint-disable max-len, jsx-a11y/no-static-element-interactions, no-nested-ternary */
import React, { Component } from 'react';
import { Grid, Segment, Header, Label, Icon, Button, Divider, List, Dropdown, Loader, Confirm, Modal, Input } from 'semantic-ui-react';
import moment from 'moment';
import { withTranslation, Trans } from 'react-i18next';
import Auth from '@aws-amplify/auth';
import PropTypes from 'prop-types';
import TimelineLog from './TimelineLog';
import { TRANSLATION_FILES } from '../../../constants/translation';
import config from '../../../../config';
import { fetchM } from '../../../actions/fetchM';
import { flashErrorMessage, flashSuccessMessage } from '../../../actions/flash';
import DocumentYellow from '../../../assets/icons/document_yellow.svg';
import DocumentGreen from '../../../assets/icons/document_green.svg';
import DocumentRed from '../../../assets/icons/document_red.svg';
import AttachmentItem from '../../../components/AttachmentItem';
import Timestamp from '../../../components/Timestamp';
import { majorToMinor, minorToMajor, formatMinorAmount } from '../../../utils/numbers/currencyMinor';
import { OPPORTUNITY_STATUS } from '../../../constants/opportunityStatuses';
import { opportunityCanHaveRefund, refundKeyOfPayment, shouldRefetchRefund } from '../../../components/Opportunities/refundRefetch';

const PAYMENT_DEFAULT = {
  payments: [],
  currencyCode: 'VND',
  amountMinorPaid: 0,
};

// Temporarily hidden: the payment timeline reads token-payment status-change
// logs from log-api, which now excludes `ur_token_payments` from the feed, so
// the timeline renders empty. Keeping the code in place — flip to `true` to
// re-enable once those events are surfaced again.
const SHOW_PAYMENT_TIMELINE = false;

export async function fetchActivityLogs(opportunityId, { page = 1, perPage = 20 } = {}) {
  const url = new URL(`${config.get('logApiURL')}/api/v1/activity-logs`);
  const params = { entity_type: 'opportunity', entity_id: opportunityId, per_page: perPage, page, event_type: 'unit_reservation_token_payment_status_change' };
  url.search = new URLSearchParams(params).toString();

  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    mode: 'cors',
  });

  if (!res.ok) {
    const message = await res.text();
    Bugsnag.notify(`Failed to GET ${url}: ${message}`);
    throw new Error(message || 'Failed to fetch activity logs');
  }
  return res.json();
}

// Named export for lifecycle tests; the default export below is the wrapped one the app uses.
export class Payment extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    loading: PropTypes.bool,
    TokenPaymentCSRF: PropTypes.string,
    onUpdateOpportunity: PropTypes.func,
    // The refund record is read only when this opportunity's sr_status can carry one.
    opportunity: PropTypes.shape({ sr_status: PropTypes.string }),
    paymentData: PropTypes.shape({
      currencyCode: PropTypes.string,
      amountMinorPaid: PropTypes.number,
      amountMinor: PropTypes.number,
      status: PropTypes.string,
      id: PropTypes.string,
      payments: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          amountMinor: PropTypes.number,
          method: PropTypes.string,
          status: PropTypes.string,
          paidAt: PropTypes.string,
          createdAt: PropTypes.string,
          notes: PropTypes.string,
          paidByBuyerName: PropTypes.string,
          metadata: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string,
            label: PropTypes.string,
            value: PropTypes.string,
          })),
        }),
      ),
    }),
    t: PropTypes.func.isRequired,
    fetchBooking: PropTypes.func,
    fetchPaymentDetails: PropTypes.func,
    auth: PropTypes.shape({
      isDeveloperSupervisor: PropTypes.bool,
      isDeveloperSalesManager: PropTypes.bool,
      isDeveloperSalesAdmin: PropTypes.bool,
      isHeadquarters: PropTypes.bool,
      isSuperAdmin: PropTypes.bool,
      isAdminBasic: PropTypes.bool,
      isAdmin: PropTypes.bool,
      isBranchLeader: PropTypes.bool,
      isBranchLeaderSupervisor: PropTypes.bool,
      isBranchLeaderSupervisorII: PropTypes.bool,
    }),
    data: PropTypes.shape({
      sr_status: PropTypes.string,
      sr_verify_rejected_at: PropTypes.string,
    }),
  };

  constructor(props) {
    super(props);
    this.state = {
      activeIndex: -1,
      confirmOpen: false,
      isAccountInfoOpen: false,
      pendingStatus: null,
      receiptsMap: {}, // { [paymentID]: sharedMediaID } for auto-generated receipts
      buyerAttachmentsMap: {}, // { [mediaID]: fileName } for buyer-uploaded receipt proofs
      buyerAttachmentsLoading: false, // true while buyer attachment filenames are being fetched
      partialModalOpen: false,
      partialPaymentID: null,
      partialOriginalAmount: 0,
      partialTokenAmount: 0,
      partialAlreadyPaid: 0,
      partialCurrencyCode: null,
      partialAmount: '',
      partialLoading: false,
      partialError: null,
      timelineLogs: [],
      timelinePage: 1,
      timelineHasMore: false,
      timelineLoading: false,
    };
  }

  componentDidMount() {
    this.mounted = true;
    const { auth, paymentData, opportunity } = this.props;
    const hasPermission = auth && (
      auth.isDeveloperSupervisor ||
      auth.isDeveloperSalesManager ||
      auth.isDeveloperSalesAdmin ||
      auth.isHeadquarters ||
      auth.isAdminBasic
    );

    // Check if opportunityID exists before calling
    if (paymentData && paymentData.opportunityID) {
      if (SHOW_PAYMENT_TIMELINE) this.loadInitialTimeline();
      // The refund record is only read for an sr_status that can carry one; otherwise 404.
      if (hasPermission && opportunityCanHaveRefund(opportunity)) {
        this.getRefundData();
      }
    }
    this.fetchReceiptAttachments();
  }

  componentDidUpdate(prevProps) {
    const { auth, paymentData, opportunity } = this.props;

    const prevId = prevProps.paymentData && prevProps.paymentData.opportunityID;
    const currentId = paymentData && paymentData.opportunityID;

    if (!currentId) {
      // Navigation nulls paymentData before the next booking loads, and the next booking may have
      // no token at all. The previous booking's record must not be rendered against it.
      this.clearRefundRecord();
      return;
    }

    if (currentId !== prevId) this.clearRefundRecord();

    if (currentId !== prevId || prevProps.paymentData !== paymentData) {
      this.fetchReceiptAttachments();
      if (SHOW_PAYMENT_TIMELINE) this.loadInitialTimeline();
    }

    const hasPermission = auth && (
      auth.isDeveloperSupervisor ||
      auth.isDeveloperSalesManager ||
      auth.isDeveloperSalesAdmin ||
      auth.isHeadquarters ||
      auth.isAdminBasic
    );
    if (!hasPermission || !opportunityCanHaveRefund(opportunity)) {
      // Payment stays mounted across a booking switch, so a record read for the previous booking
      // must not survive into one that cannot have a refund (or that this user may not read).
      this.clearRefundRecord();
      return;
    }

    // The refund record is re-read only when the reservation token's own fields changed, or the
    // opportunity moved between refund states (approve / decline), not because the parent handed
    // down a fresh object (CPI-1388). Outside the refund states the GET is a guaranteed 404.
    const prevSRStatus = prevProps.opportunity && prevProps.opportunity.sr_status;
    if (shouldRefetchRefund(prevProps.paymentData, paymentData, refundKeyOfPayment) ||
      prevSRStatus !== opportunity.sr_status) {
      this.getRefundData();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getStatusLabel = (tx) => {
    const { t } = this.props;

    if (tx.status === 'completed') return t('Paid');
    if (tx.status === 'failed') return t('FAILED');

    // Handle Pending states
    if (tx.method === 'online') {
      return t('Pending completion');
    }

    return t('Confirm Amount');
  }

  getPaymentStatusLabel = (status) => {
    const { t } = this.props;
    if (status === 'paid_verified') return t('Verified');
    if (status === 'verification_rejected') return t('Rejected');
    return t('Pending verification');
  };

  getRefundData = () => {
    const { store } = this.context;
    const { paymentData } = this.props;
    if (!paymentData || !paymentData.opportunityID) return;

    const url = `/unit_reservation_refunds/opportunity/${paymentData.opportunityID}/`;
    this.refundRequestVersion = (this.refundRequestVersion || 0) + 1;
    const requestVersion = this.refundRequestVersion;
    const isCurrent = () => this.mounted && requestVersion === this.refundRequestVersion;

    const action = fetchM(url, {
      credentials: 'include',
      method: 'GET',
      ignoreNotFound: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (store) {
      store.dispatch(action)
        .then((response) => {
          if (response && response.ok) {
            return response.json();
          }
          throw new Error('Network response was not ok');
        })
        .then((data) => {
          if (data && isCurrent()) {
            this.setState({ refundRecord: data });
          }
        })
        .catch(() => {
          if (isCurrent()) {
            this.setState({ refundRecord: null });
          }
        });
    }
  };

  getViewLink = (id, fileName) => {
    const viewUrl = `${config.get('bookApiURL')}/shared_medias/${id}/file/${encodeURIComponent(fileName)}`;
    return viewUrl;
  }

  getMetadataValue = (metadata, key) => {
    if (!metadata || !metadata.length) return null;
    const item = metadata.find(m => m.key === key);
    return (item && item.value) ? item.value : null;
  };

  clearRefundRecord = () => {
    // Also invalidates any in-flight read so a late response cannot repopulate the card.
    this.refundRequestVersion = (this.refundRequestVersion || 0) + 1;
    if (this.state.refundRecord != null) this.setState({ refundRecord: null });
  }

  handleCardClick = (index) => {
    this.setState(prevState => ({
      activeIndex: prevState.activeIndex === index ? -1 : index,
    }));
  };

  handleApprove = (id) => {
    this.updatePaymentStatus(id, 'completed');
  }

  handlePartial = (id, paymentAmount, tokenAmount, alreadyPaid, code) => {
    this.setState({
      partialModalOpen: true,
      partialPaymentID: id,
      partialOriginalAmount: paymentAmount,
      partialTokenAmount: tokenAmount,
      partialAlreadyPaid: alreadyPaid,
      partialCurrencyCode: code,
      partialAmount: String(minorToMajor(paymentAmount, code)),
      partialError: null,
    });
  }

  handlePartialCancel = () => {
    this.setState({ partialModalOpen: false, partialPaymentID: null, partialAmount: '', partialError: null });
  }

  handlePartialAmountChange = (e) => {
    this.setState({ partialAmount: e.target.value, partialError: null });
  }

  submitPartialPayment = async () => {
    const { t } = this.props;
    const { partialPaymentID, partialAmount, partialOriginalAmount, partialTokenAmount, partialAlreadyPaid, partialCurrencyCode } = this.state;
    const outstanding = partialTokenAmount - partialAlreadyPaid;

    const rawMajor = Number(partialAmount);
    if (!partialAmount || isNaN(rawMajor) || rawMajor <= 0) {
      this.setState({ partialError: t('Amount must be a positive number') });
      return;
    }

    const parsedMinor = majorToMinor(rawMajor, partialCurrencyCode);

    if (parsedMinor > outstanding) {
      this.setState({ partialError: t('Amount cannot exceed the outstanding balance') });
      return;
    }
    if (parsedMinor === partialOriginalAmount) {
      this.setState({ partialError: t('Amount cannot be equal to the submitted payment amount') });
      return;
    }

    this.setState({ partialLoading: true, partialError: null });
    try {
      await this.updatePaymentStatus(partialPaymentID, 'completed', parsedMinor);
      this.setState({ partialModalOpen: false, partialPaymentID: null, partialAmount: '', partialLoading: false });
    } catch (err) {
      this.setState({ partialLoading: false, partialError: t('Error updating payment status', { ns: TRANSLATION_FILES.translation }) });
    }
  }

  updatePaymentStatus = async (paymentID, nextStatus, amountMinor) => {
    const { store } = this.context;
    const { paymentData, TokenPaymentCSRF, t } = this.props;

    if (!paymentData || !paymentData.id) return;
    const url = `/unit_reservation_tokens/${paymentData.id}/payments/${paymentID}/manage/`;
    const body = { status: nextStatus };
    if (amountMinor !== undefined) body.amountMinor = amountMinor;
    try {
      await store.dispatch(fetchM(url, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': TokenPaymentCSRF,
        },
        body: JSON.stringify(body),
      }));
      store.dispatch(flashSuccessMessage({ header: t('Success', { ns: TRANSLATION_FILES.constants }), message: t('Status updated') }));
      if (this.props.fetchPaymentDetails) this.props.fetchPaymentDetails();
      if (this.props.fetchBooking) this.props.fetchBooking();
    } catch (err) {
      store.dispatch(flashErrorMessage({
        header: t('Error', { ns: TRANSLATION_FILES.constants }),
        message: t('Error updating payment status', { ns: TRANSLATION_FILES.translation }),
      }));
      if (amountMinor !== undefined) throw err;
    }
  };

  handleStatusUpdate = async (nextStatus) => {
    const { store } = this.context;
    const { paymentData, TokenPaymentCSRF, t } = this.props;
    if (!paymentData || !paymentData.id) return;
    const tokenID = paymentData.id;
    const url = `/unit_reservation_tokens/${tokenID}/manage/`;

    try {
      await store.dispatch(fetchM(url, {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': TokenPaymentCSRF,
        },
        body: JSON.stringify({ status: nextStatus }),
      }));
      store.dispatch(flashSuccessMessage({
        header: t('Success', { ns: TRANSLATION_FILES.constants }),
        message: t('Status_Payment_Changed_Success'),
      }));
      if (this.props.fetchPaymentDetails) {
        this.props.fetchPaymentDetails();
      }
      if (this.props.fetchBooking) this.props.fetchBooking();
    } catch (err) {
      store.dispatch(flashErrorMessage({
        header: t('Upload Failed'),
        message: t('Status changed failed'),
      }));
    }
  };

  handleRejection = (nextStatus) => {
    const { onUpdateOpportunity } = this.props;
    if (onUpdateOpportunity) {
      onUpdateOpportunity(nextStatus);
    }
  }

  // Fetch auto-generated receipts (file_type=token_payment_receipt) for the opportunity
  // and build a map of paymentID -> sharedMediaID using the filename convention
  // receipt_token_payment_{paymentID}.pdf.
  fetchReceiptAttachments = async () => {
    const { store } = this.context;
    const paymentData = this.props.paymentData;
    const opportunityID = paymentData && paymentData.opportunityID;

    // Reset maps and mark loading to avoid stale data when switching opportunities or
    // when there is no opportunityID (e.g. navigating away).
    if (this.mounted) this.setState({ receiptsMap: {}, buyerAttachmentsMap: {}, buyerAttachmentsLoading: true });

    if (!opportunityID) {
      if (this.mounted) this.setState({ buyerAttachmentsLoading: false });
      return;
    }

    try {
      const res = await store.dispatch(fetchM(
        `/shared_medias/?parent_type=opportunity&parent_id=${opportunityID}&file_type=token_payment_receipt`,
        { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } },
      ));
      if (res && res.ok) {
        const medias = await res.json();
        const map = {};
        (medias || []).forEach((m) => {
          // filename: receipt_token_payment_{paymentID}.pdf
          const match = m.fileName && m.fileName.match(/^receipt_token_payment_(.+)\.pdf$/);
          if (match) map[match[1]] = m.id;
        });
        if (this.mounted) this.setState({ receiptsMap: map });
      }

      // Fetch filenames for buyer-uploaded receipt proofs.
      const payments = (paymentData && paymentData.payments) || [];
      const buyerMediaIDs = [...new Set(payments.map(p => p.receiptMediaID).filter(Boolean))];
      if (buyerMediaIDs.length > 0) {
        const buyerMap = {};
        await Promise.all(buyerMediaIDs.map(async (mediaID) => {
          try {
            const r = await store.dispatch(fetchM(
              `/shared_medias/${mediaID}/`,
              { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } },
            ));
            if (r && r.ok) {
              const media = await r.json();
              if (media && media.fileName) buyerMap[mediaID] = media.fileName;
            }
          } catch (_) { /* non-critical */ }
        }));
        if (this.mounted) this.setState({ buyerAttachmentsMap: buyerMap });
      }
    } catch (_) {
      // silently fail — receipt display is non-critical
    } finally {
      if (this.mounted) this.setState({ buyerAttachmentsLoading: false });
    }
  };

  loadInitialTimeline = () => {
    const { paymentData } = this.props;
    const opportunityId = paymentData && paymentData.opportunityID;

    if (!opportunityId) {
      this.setState({ timelineLogs: [], timelinePage: 1, timelineHasMore: false });
      return;
    }

    this.setState({ timelineLoading: true, timelinePage: 1 }, async () => {
      try {
        const data = await fetchActivityLogs(opportunityId, { page: 1, perPage: 10 });
        if (!this.mounted) return;

        const logsList = data.activity_logs || [];

        const filtered = logsList.filter(
          log => log.source_table === 'ur_token_payments' || log.source_table === 'unit_resv_tokens',
        );

        const pagination = data.pagination || {};
        const hasMore = pagination.has_next || false;

        this.setState({
          timelineLogs: filtered,
          timelineHasMore: hasMore,
          timelineLoading: false,
        });
      } catch (err) {
        if (this.mounted) this.setState({ timelineLoading: false });
      }
    });
  }

  loadMoreTimeline = () => {
    const { paymentData } = this.props;
    const opportunityId = paymentData && paymentData.opportunityID;
    const { timelinePage, timelineLoading, timelineHasMore } = this.state;

    if (!opportunityId || timelineLoading || !timelineHasMore) return;

    const nextPage = timelinePage + 1;
    this.setState({ timelineLoading: true }, async () => {
      try {
        const data = await fetchActivityLogs(opportunityId, { page: nextPage, perPage: 10 });
        if (!this.mounted) return;

        const logsList = data.activity_logs || [];

        const filtered = logsList.filter(
          log => log.source_table === 'ur_token_payments' || log.source_table === 'unit_resv_tokens',
        );

        const pagination = data.pagination || {};
        const hasMore = pagination.has_next || false;

        this.setState(prevState => ({
          timelineLogs: [...prevState.timelineLogs, ...filtered],
          timelinePage: nextPage,
          timelineHasMore: hasMore,
          timelineLoading: false,
        }));
      } catch (err) {
        if (this.mounted) this.setState({ timelineLoading: false });
      }
    });
  }

  isUserHqOrBranchLeaders = () => {
    const { auth } = this.props;
    return auth && (auth.isHeadquarters || auth.isBranchLeader || auth.isBranchLeaderSupervisor || auth.isBranchLeaderSupervisorII);
  }
  showConfirm = (status) => {
    this.setState({ confirmOpen: true, pendingStatus: status });
  }
  handleCancel = () => this.setState({ confirmOpen: false, pendingStatus: null });

  handleConfirm = () => {
    const { pendingStatus } = this.state;

    if (pendingStatus === 'verification_rejected') {
      this.handleRejection('verification_rejected');
    } else {
      this.handleStatusUpdate(pendingStatus);
    }

    this.setState({ confirmOpen: false, pendingStatus: null });
  };

  // CPI-977: promoted to utils/numbers/currencyMinor.js so the tabs cannot drift. Output unchanged.
  formatCurrency = (amountMinor, code = 'VND') => formatMinorAmount(amountMinor, code);

  handleCopyMetadataValue = (e, value) => {
    e.stopPropagation();
    const { store } = this.context;
    const { t } = this.props;

    const textArea = document.createElement('textarea');
    textArea.value = String(value || '');
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('Copy');
    textArea.remove();

    store.dispatch(flashSuccessMessage({
      header: t('Copy Success', { ns: TRANSLATION_FILES.translation }),
      message: t('Successfully copied to your clipboard', { ns: TRANSLATION_FILES.translation }),
    }));
  };

  renderPaymentMethodPills = (metadata) => {
    const { t } = this.props;
    const pm = this.getMetadataValue(metadata, 'payment_method');
    if (!pm) return null;
    const v = pm.toLowerCase();
    const pills = [];
    if (v.includes('-credit-card')) pills.push({ text: t('Credit Card'), color: 'green' });
    if (v.includes('atm-card')) pills.push({ text: t('ATM Card'), color: 'blue' });
    return pills.map(p => (
      <Label key={p.text} size="mini" color={p.color}>{p.text}</Label>
    ));
  };

  renderMetadataField = (label, value, copyable = false, truncate = true) => {
    const { t } = this.props;
    const MAX_LEN = 18;
    const canCopy = copyable && !!value;
    const isLong = truncate && !!value && value.length > MAX_LEN;
    const displayValue = isLong ? `${value.slice(0, MAX_LEN)}…` : value;
    return (
      <div className="div-text-holder" key={label}>
        <div className="div-text">{t(label)}</div>
        {isLong || canCopy ? (
          <div className="div-value metadata-value-truncate">
            <span title={value}>{displayValue || 'N/A'}</span>
            {canCopy && (
              <Button
                icon
                type="button"
                className="metadata-copy-btn"
                aria-label={t('Copy')}
                title={t('Copy')}
                onClick={e => this.handleCopyMetadataValue(e, value)}
              >
                <Icon name="copy outline" />
              </Button>
            )}
          </div>
        ) : (
          <div className="div-value">{value || 'N/A'}</div>
        )}
      </div>
    );
  };

  render() {
    const { loading, t, data, paymentData } = this.props;
    const { activeIndex, receiptsMap, buyerAttachmentsMap, buyerAttachmentsLoading, timelineLogs, timelineHasMore, timelineLoading } = this.state;
    if (loading) return <Loader active inline="centered" style={{ marginTop: '2rem' }} />;
    const {
      payments = PAYMENT_DEFAULT.payments,
      currencyCode = PAYMENT_DEFAULT.currencyCode,
      amountMinorPaid = PAYMENT_DEFAULT.amountMinorPaid,
      amountMinor = PAYMENT_DEFAULT.amountMinor,
      status = PAYMENT_DEFAULT.status,
    } = paymentData || {};
    // CPI-1226: cancelled_error is as terminal as `cancelled`, so both lists below carry it too.
    const RESTRICTED_DROPDOWN_STATUSES = [
      OPPORTUNITY_STATUS.PENDING_CANCELLATION,
      OPPORTUNITY_STATUS.PENDING_REFUND,
      OPPORTUNITY_STATUS.REFUNDED,
      OPPORTUNITY_STATUS.REFUND_DECLINED,
      OPPORTUNITY_STATUS.CANCELLED,
      OPPORTUNITY_STATUS.CANCELLED_ERROR,
      OPPORTUNITY_STATUS.EXPIRED,
    ];
    const DISPLAY_STATUS_MAP = [
      OPPORTUNITY_STATUS.PENDING_CANCELLATION,
      OPPORTUNITY_STATUS.PENDING_REFUND,
      OPPORTUNITY_STATUS.REFUNDED,
      OPPORTUNITY_STATUS.REFUND_DECLINED,
      OPPORTUNITY_STATUS.CANCELLED,
      OPPORTUNITY_STATUS.CANCELLED_ERROR,
      OPPORTUNITY_STATUS.PAID_VERIFIED,
      OPPORTUNITY_STATUS.APPROVED,
    ];
    const srStatus = data && data.sr_status;
    const hasBeenDenied = srStatus === 'verification_rejected' || (data && data.sr_verify_rejected_at != null);
    const paymentsSafe = payments || [];
    const outstandingMinor = amountMinor - amountMinorPaid;
    const { refundRecord } = this.state;
    let summaryLabelColor = 'orange';
    let summaryLabelText = t('Pending Payment');
    if (status === 'paid_verified') {
      summaryLabelColor = 'teal';
      summaryLabelText = t('FULLY PAID');
    } else if (status === 'paid_unverified') {
      summaryLabelColor = 'green';
      summaryLabelText = t('FULLY PAID');
    } else if (status === 'partially_paid') {
      summaryLabelColor = 'blue';
      summaryLabelText = t('PARTIALLY PAID');
    }
    const sortedPayments = [...paymentsSafe].sort((a, b) =>
    moment(b.paidAt).valueOf() - moment(a.paidAt).valueOf(),
  );
    return (
      <Grid stackable>
        {/* LEFT COLUMN: Payment List */}
        <Grid.Column width={11}>
          <div className="payment-header">
            <Header as="h3">{t('Payments made')} ({paymentsSafe.length})
              {srStatus === 'refunded' && (
                <Label basic size="small" className="refund-status-label">
                  {t('REFUNDED', { ns: TRANSLATION_FILES.translation })}
                </Label>
              )}

            </Header>
            <div>
              {this.isUserHqOrBranchLeaders() && (srStatus === 'paid_unverified') && (
                <Dropdown
                  inline
                  className="payment-status-dropdown"
                  trigger={
                    <span className="status-wrapper">
                      <img
                        src={srStatus === 'paid_verified' ? DocumentGreen : DocumentYellow}
                        alt={srStatus === 'paid_verified' ? 'verified' : 'pending'}
                      />
                      <span>
                        {this.getPaymentStatusLabel(srStatus)}
                      </span>
                    </span>
                  }
                >
                  <Dropdown.Menu>
                    <Dropdown.Item disabled={paymentsSafe.length === 0} onClick={() => this.showConfirm('paid_verified')}>
                      <div className="status-wrapper">
                        <img src={DocumentGreen} alt="Verified" />
                        <span>{t('Verified')}</span>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item disabled={paymentsSafe.length === 0} onClick={() => this.showConfirm('verification_rejected')}>
                      <div className="status-wrapper">
                        <img src={DocumentRed} alt="Rejected" />
                        <span>{t('Rejected')}</span>
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
              {this.isUserHqOrBranchLeaders() && (srStatus === 'verification_rejected' && amountMinorPaid >= amountMinor) && (
                <Button color="teal" icon="check" onClick={() => this.showConfirm('paid_verified')} content={t('Mark as verified')} className="btn-verify-link" />
              )}
              { (srStatus !== 'paid_verified' && hasBeenDenied && status !== 'paid_verified') && (
              <span className="status-wrapper">
                <img
                  src={DocumentRed}
                  alt="Rejected"
                />
                <span>
                  {t('Rejected')}
                </span>
              </span>
              )}
              { ((status === 'paid_verified') && DISPLAY_STATUS_MAP.includes(srStatus)) && (
              <span className="status-wrapper">
                <img
                  src={DocumentGreen}
                  alt="Verified"
                />
                <span>
                  {t('Verified')}
                </span>
              </span>
              )}
            </div>
          </div>
          <div className="payment-display">
            {sortedPayments.map((tx, index) => {
              const showConfirmDropdown =
                tx.method !== 'online' &&
                tx.status !== 'completed' &&
                !RESTRICTED_DROPDOWN_STATUSES.includes(srStatus);
              const isOpen = activeIndex === index;
              return (
                <Segment key={tx.id || index} clearing style={{ padding: '0' }}>
                  <div
                    onClick={() => this.handleCardClick(index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                        e.preventDefault();
                        e.stopPropagation();
                        this.handleCardClick(index);
                      }
                    }}
                    className="div-payment-card-display"
                  >
                    <div>
                      <Icon name={isOpen ? 'chevron down' : 'chevron right'} />
                      <span className="currency-payment-format">
                        {this.formatCurrency(tx.amountMinor, currencyCode)}
                      </span>
                      <div className="payment-method">{t(tx.method)}</div>
                    </div>
                    <div>
                      {tx.status !== 'pending' && (
                      <Label
                        basic
                        color={(() => {
                          if (tx.status === 'completed') return 'teal';
                          if (tx.status === 'failed') return 'red';
                          return 'orange';
                        })()}
                        horizontal
                      >
                        {this.getStatusLabel(tx)}
                      </Label>
                      )}
                      {/* Three Dots with Actions (using Semantic UI Dropdown) */}
                      {showConfirmDropdown && (
                      <Dropdown
                        inline
                        pointing="top right"
                        className="payment-card-dropdown"
                        trigger={
                          <span className="payment-card-dropdown-hover-text">
                            <span>{t('Confirm Amount')}</span>
                            <Icon name="dropdown" style={{ margin: 0 }} />
                          </span>
                        }
                      >
                        <Dropdown.Menu>
                          <Dropdown.Item
                            icon="check"
                            text={t('Full')}
                            disabled={tx.status === 'completed'}
                            onClick={() => this.handleApprove(tx.id)}
                          />
                          <Dropdown.Item
                            icon="pencil"
                            text={t('Partial')}
                            disabled={tx.status === 'completed' || tx.status === 'failed'}
                            onClick={() => this.handlePartial(tx.id, tx.amountMinor, amountMinor, tx.status === 'completed' ? amountMinorPaid - tx.amountMinor : amountMinorPaid, currencyCode)}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                     )}
                    </div>
                  </div>

                  {isOpen && (
                    <div
                      className="payment-card-open"
                    >
                      <div className="payment-open-title">{t('Transaction details')} </div>
                      <Grid columns={2} stackable>
                        <Grid.Row>
                          <Grid.Column width={8}>
                            <div className="div-text-holder">
                              <div className="div-text">
                                {t('Paid by')}
                              </div>
                              <div className="div-value">
                                {tx.paidByBuyerName || 'N/A'}
                              </div>
                            </div>
                            <div className="div-text-holder">
                              <div className="div-text">
                                {t('Payment Method')}
                              </div>
                              <div className="div-value" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem' }}>
                                {tx.method ? t(tx.method) : 'N/A'}
                                {this.renderPaymentMethodPills(tx.metadata)}
                              </div>
                            </div>
                            <div className="div-text-holder">
                              <div className="div-text">
                                {t('Payment received')}
                              </div>
                              <div className="div-value">
                                {this.formatCurrency(tx.amountMinor, currencyCode)}
                              </div>
                            </div>
                          </Grid.Column>
                          {/* right column*/}
                          <Grid.Column width={8}>
                            <div className="div-text-holder">
                              <div className="div-text">
                                {t('Payment received at')}
                              </div>
                              <div className="div-value">
                                {tx.paidAt ? <Timestamp format="DD MMM YYYY, hh:mm A" timestamp={tx.paidAt} /> : 'N/A'}
                              </div>
                            </div>
                            <div className="div-text-holder">
                              <div className="div-text">
                                {t('Reference')}
                              </div>
                              <div className="div-value">
                                {tx.referenceNumber || 'N/A'}
                              </div>
                            </div>
                            {tx.receiptMediaID && (
                            <div className="div-text-holder">
                              <div className="div-text">
                                {t('Attachment')}
                              </div>
                              <div className="attachment-inline-wrapper">
                                {buyerAttachmentsLoading ? (
                                  <Loader active inline size="tiny" />
                                ) : buyerAttachmentsMap[tx.receiptMediaID] ? (
                                  <AttachmentItem
                                    small
                                    fileName={buyerAttachmentsMap[tx.receiptMediaID]}
                                    downloadUrl={this.getViewLink(tx.receiptMediaID, buyerAttachmentsMap[tx.receiptMediaID])}
                                  />
                                ) : (
                                  <span>{t('Attachment unavailable')}</span>
                                )}
                              </div>
                            </div>
                            )}
                            {/* <div className="div-text-holder">
                              <div className="div-text">
                                {t('Notes')}
                              </div>
                              <div>
                                {tx.notes || 'N/A'}
                              </div>
                            </div> */}
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                      {receiptsMap[tx.id] && (
                      <div className="div-text-holder div-text-holder--align-top">
                        <div className="div-text">
                          {t('Official receipt')}
                        </div>
                        <div className="attachment-inline-wrapper">
                          <div className="attachment-list">
                            <AttachmentItem
                              small
                              fileName={`receipt_token_payment_${tx.id}.pdf`}
                              downloadUrl={this.getViewLink(receiptsMap[tx.id], `receipt_token_payment_${tx.id}.pdf`)}
                            />
                          </div>
                        </div>
                      </div>
                      )}
                      {tx.method === 'online' && (() => {
                        const meta = tx.metadata || [];
                        const transactionCode = this.getMetadataValue(meta, 'transaction_code');
                        const tokenCode = this.getMetadataValue(meta, 'token_code');
                        const bankReferCode = this.getMetadataValue(meta, 'bank_refer_code');
                        const paymentMethod = this.getMetadataValue(meta, 'payment_method');
                        const orderCode = this.getMetadataValue(meta, 'order_code');
                        const orderDescription = this.getMetadataValue(meta, 'order_description');
                        const allFields = [
                          tokenCode && { label: 'Token Code', value: tokenCode, copyable: true },
                          transactionCode && { label: 'Transaction Code', value: transactionCode, copyable: true },
                          bankReferCode && { label: 'Bank Reference Code', value: bankReferCode, copyable: true },
                          paymentMethod && { label: 'Payment Method Code', value: paymentMethod, truncate: false },
                          { label: 'Order Code', value: orderCode, copyable: true },
                          { label: 'Order Description', value: orderDescription, truncate: false },
                        ].filter(Boolean);
                        const leftFields = allFields.filter((_, i) => i % 2 === 0);
                        const rightFields = allFields.filter((_, i) => i % 2 === 1);
                        return (
                          <div className="payment-metadata-segment">
                            <Grid columns={2} stackable>
                              <Grid.Row>
                                <Grid.Column width={8}>
                                  {leftFields.map(f => this.renderMetadataField(f.label, f.value, f.copyable, f.truncate))}
                                </Grid.Column>
                                <Grid.Column width={8}>
                                  {rightFields.map(f => this.renderMetadataField(f.label, f.value, f.copyable, f.truncate))}
                                </Grid.Column>
                              </Grid.Row>
                            </Grid>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </Segment>
              );
            })}
          </div>
        </Grid.Column>

        {/* RIGHT COLUMN: Summary & Timeline */}
        <Grid.Column width={5}>
          <Segment raised>
            <div className="div-summary-container">
              <Header as="h4" style={{ margin: 0 }}>{t('Summary')}</Header>
              {paymentsSafe.length > 0 && (
                <Label
                  basic
                  size="mini"
                  color={summaryLabelColor}
                >
                  {summaryLabelText}
                </Label>
              )}
            </div>
            <Divider />

            <List verticalAlign="middle">
              <List.Item>
                <List.Content floated="right">{this.formatCurrency(amountMinor, currencyCode)}</List.Content>
                <List.Content>{t('Total Booking Fee')}</List.Content>
              </List.Item>
              <List.Item>
                <List.Content floated="right">{this.formatCurrency(amountMinorPaid, currencyCode)}</List.Content>
                <List.Content>{t('Payments made')}</List.Content>
              </List.Item>
            </List>

            <Divider />

            <div className="div-currency-container">
              <span>{t('Outstanding')}</span>
              <span>{this.formatCurrency(outstandingMinor, currencyCode)}</span>
            </div>
          </Segment>

          {/* refund tab information */}
          {refundRecord && (
            <div className="refund-container">
              <Segment
                onClick={() => this.setState({ isAccountInfoOpen: !this.state.isAccountInfoOpen })}
                className={`refund-card ${this.state.isAccountInfoOpen ? 'is-open' : ''}`}
              >
                <span className="refund-title-text">
                  {t('Refund account information')}
                </span>
                <Icon name={this.state.isAccountInfoOpen ? 'chevron up' : 'chevron down'} />
              </Segment>

              {this.state.isAccountInfoOpen && (
                <Segment
                  className="refund-card-open"
                >
                  <Grid columns={2}>
                    <Grid.Row className="refund-row">
                      <Grid.Column width={6} className="label">{t('Bank')}</Grid.Column>
                      <Grid.Column width={10} textAlign="right" className="value">
                        {refundRecord.bankName || 'N/A'}
                      </Grid.Column>
                    </Grid.Row>

                    <Grid.Row className="refund-row">
                      <Grid.Column width={6} className="label">{t('Beneficiary name')}</Grid.Column>
                      <Grid.Column width={10} textAlign="right" className="value">
                        {refundRecord.bankAccountName || 'N/A'}
                      </Grid.Column>
                    </Grid.Row>

                    <Grid.Row className="refund-row">
                      <Grid.Column width={8} className="label">{t('Beneficiary acc no.')}</Grid.Column>
                      <Grid.Column width={8} textAlign="right" className="value">
                        {refundRecord.bankAccountNumber || 'N/A'}
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Segment>
              )}
            </div>
          )}

          {SHOW_PAYMENT_TIMELINE && (
            <React.Fragment>
              <Header as="h4">{t('Timeline')}</Header>
              <div className="payment-timeline" style={{ marginTop: '0.5rem' }}>
                <TimelineLog
                  activityLog={timelineLogs}
                  hasMore={timelineHasMore}
                  isLoadingMore={timelineLoading}
                  onLoadMore={this.loadMoreTimeline}
                />
              </div>
            </React.Fragment>
          )}
        </Grid.Column>
        <Modal
          open={this.state.partialModalOpen}
          size="small"
          onClose={this.handlePartialCancel}
          closeOnDimmerClick={!this.state.partialLoading}
          closeOnEscape={!this.state.partialLoading}
          style={{
            padding: '1.5rem 1.5rem 0.5rem 1.5rem',
            borderTop: 'none',
            boxShadow: 'none',
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: '0 0 1rem 0', color: '#000' }}>
            {t('Partial payment received')}
          </h2>
          <Modal.Content>
            <p style={{ color: '#444', marginBottom: '1.5rem', lineHeight: '1.4' }}>
              <Trans i18nKey="partial_payment_instruction" components={[<strong />]} t={t} />
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '0.25rem' }}>{t('SUBMITTED AMOUNT')}</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 'bold' }}>
                {this.formatCurrency(this.state.partialOriginalAmount, this.state.partialCurrencyCode)}
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="partial-amount-input" style={{ fontSize: '0.85rem', fontWeight: 'bold', display: 'block', marginBottom: '0.4rem' }}>
                {t('TOTAL RECEIVED TO DATE')} <span style={{ color: 'red' }}>*</span>
              </label>
              <Input
                id="partial-amount-input"
                fluid
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\\.?[0-9]*"
                value={this.state.partialAmount}
                onChange={this.handlePartialAmountChange}
                label={this.state.partialCurrencyCode}
                placeholder="0"
              />
              {this.state.partialError && (
                <p style={{ color: 'red', marginTop: '0.4rem', fontSize: '0.85rem' }}>{this.state.partialError}</p>
              )}
            </div>
            {(() => {
              const code = this.state.partialCurrencyCode;
              const rawMajor = Number(this.state.partialAmount);
              const inputMinor = (!isNaN(rawMajor) && rawMajor > 0) ? majorToMinor(rawMajor, code) : null;
              const previewOutstandingMinor = inputMinor !== null ? this.state.partialTokenAmount - this.state.partialAlreadyPaid - inputMinor : null;
              return (
                <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden', marginTop: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0.8rem', borderBottom: '1px solid #e0e0e0', color: '#444' }}>
                    <span>{t('Booking fee')}</span>
                    <span>{this.formatCurrency(this.state.partialTokenAmount, code)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0.8rem', borderBottom: '1px solid #e0e0e0', color: '#444' }}>
                    <span>{t('Confirmed paid amount')}</span>
                    <span>{inputMinor !== null ? this.formatCurrency(inputMinor, code) : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0.8rem', fontWeight: 'bold' }}>
                    <span>{t('Remaining Outstanding')}</span>
                    <span style={{ color: previewOutstandingMinor !== null && previewOutstandingMinor > 0 ? '#e07b00' : 'inherit' }}>
                      {previewOutstandingMinor !== null ? this.formatCurrency(previewOutstandingMinor, code) : '—'}
                    </span>
                  </div>
                </div>
              );
            })()}
          </Modal.Content>
          <Modal.Actions>
            <Button basic onClick={this.handlePartialCancel} disabled={this.state.partialLoading}>{t('Cancel')}</Button>
            <Button
              basic
              onClick={this.submitPartialPayment}
              loading={this.state.partialLoading}
              disabled={this.state.partialLoading}
            >
              {t('Request Outstanding')}
            </Button>
          </Modal.Actions>
        </Modal>
        <Confirm
          open={this.state.confirmOpen}
          header={this.state.pendingStatus === 'paid_verified' ? t('Verify Payment') : t('Reject Payment')}
          content={t('Are you sure you want to change the status of this booking?')}
          onCancel={this.handleCancel}
          onConfirm={this.handleConfirm}
          cancelButton={t('cancel')}
          confirmButton={t('Confirm update')}
          size="mini"
        />
      </Grid>
    );
  }
}

export default withTranslation(TRANSLATION_FILES.screens)(Payment);



// WEBPACK FOOTER //
// ./src/containers/Opportunities/Overview/Payment.jsx